/**
 * Utility Functions
 * shadcn/ui ile uyumlu yardımcı fonksiyonlar
 */

import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Tailwind CSS sınıflarını birleştirir
 * shadcn/ui component'larında kullanılır
 *
 * @param {...string} inputs - CSS sınıfları
 * @returns {string} Birleştirilmiş sınıflar
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Tarihi formatlar
 * @param {Date|string} date - Tarih
 * @param {string} locale - Dil (varsayılan: de-DE)
 * @returns {string} Formatlanmış tarih
 */
export function formatDate(date, locale = 'de-DE') {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Para birimini formatlar
 * @param {number} amount - Miktar
 * @param {string} currency - Para birimi (varsayılan: EUR)
 * @returns {string} Formatlanmış para
 */
export function formatCurrency(amount, currency = 'EUR') {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Telefon numarasını formatlar
 * @param {string} phone - Telefon numarası
 * @returns {string} Formatlanmış numara
 */
export function formatPhone(phone) {
  if (!phone) return '';
  // Sadece rakamları al
  const digits = phone.replace(/\D/g, '');

  // Almanya formatı
  if (digits.startsWith('49')) {
    return `+49 ${digits.slice(2, 5)} ${digits.slice(5)}`;
  }

  return phone;
}

/**
 * String'i slug'a çevirir
 * @param {string} str - String
 * @returns {string} Slug
 */
export function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Debounce fonksiyonu
 * @param {Function} func - Çağrılacak fonksiyon
 * @param {number} wait - Bekleme süresi (ms)
 * @returns {Function} Debounce edilmiş fonksiyon
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle fonksiyonu
 * @param {Function} func - Çağrılacak fonksiyon
 * @param {number} limit - Limit süresi (ms)
 * @returns {Function} Throttle edilmiş fonksiyon
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Object'ten boş değerleri kaldırır
 * @param {Object} obj - Object
 * @returns {Object} Temizlenmiş object
 */
export function removeEmptyValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) =>
      v !== null && v !== undefined && v !== ''
    )
  );
}

/**
 * Deep clone
 * @param {*} obj - Kopyalanacak değer
 * @returns {*} Kopya
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * İki tarihi karşılaştırır
 * @param {Date|string} date1
 * @param {Date|string} date2
 * @returns {number} -1, 0, 1
 */
export function compareDates(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
}

/**
 * Rastgele ID üretir
 * @param {number} length - Uzunluk
 * @returns {string} ID
 */
export function generateId(length = 8) {
  return Math.random().toString(36).substr(2, length);
}

export default {
  cn,
  formatDate,
  formatCurrency,
  formatPhone,
  slugify,
  debounce,
  throttle,
  removeEmptyValues,
  deepClone,
  compareDates,
  generateId,
};
